import { useState } from "react";
import {
  comptesParNiveau,
  itemsDeplacesParEchange,
  placer,
  type PlacementsQsort,
} from "@repo/utils";
import {
  ID_RESERVE,
  prefixeItem,
  prefixeNiveau,
  type QsortItem,
  type VarianteChip,
} from "./qsort-board-elements";

/** Décode la cible d'un drop : un niveau, la réserve, ou un item (→ échange). */
function cibleDuDrop(id: string): {
  niveau: number | null;
  echangeAvec?: string;
} | null {
  if (id === ID_RESERVE) return { niveau: null };
  if (id.startsWith(prefixeNiveau)) {
    const niveau = Number(id.slice(prefixeNiveau.length));
    return Number.isInteger(niveau) ? { niveau } : null;
  }
  if (id.startsWith(prefixeItem)) return { niveau: NaN, echangeAvec: id.slice(prefixeItem.length) };
  return null;
}

/**
 * Logique de placement du tableau Q-sort, hors rendu : sélection, dépôt,
 * échange sur niveau plein et son annulation. Le tableau n'a plus qu'à
 * brancher ces gestes sur ses éléments.
 */
export function useQsortPlacement({
  parCode,
  pyramide,
  placements,
  onChange,
  libelles,
  lectureSeule,
  isMobile,
}: {
  parCode: Map<string, QsortItem>;
  pyramide: number[];
  placements: PlacementsQsort;
  onChange: (placements: PlacementsQsort) => void;
  libelles: string[];
  lectureSeule: boolean;
  isMobile: boolean;
}) {
  const [selection, setSelection] = useState<string | null>(null);
  /** Niveau plein visé au tap : on attend que l'utilisateur choisisse l'occupant à remplacer. */
  const [niveauPlein, setNiveauPlein] = useState<number | null>(null);
  /** Dernier échange : dit qui a été renvoyé, et permet de revenir en arrière. */
  const [echange, setEchange] = useState<{
    texte: string;
    deplace: string;
    precedent: PlacementsQsort;
  } | null>(null);
  /**
   * Dernier placement simple, dit en toutes lettres : la ligne d'état est une
   * région `aria-live`, c'est ainsi qu'un lecteur d'écran apprend où l'item
   * est allé — la pyramide, elle, ne se « voit » pas.
   */
  const [annonce, setAnnonce] = useState<string | null>(null);
  const comptes = comptesParNiveau(placements, pyramide.length);

  const nomDe = (code: string) => `« ${parCode.get(code)?.label ?? code} »`;
  const nomNiveau = (niveau: number | null) =>
    niveau === null ? "« À classer »" : (libelles[niveau] ?? `Niveau ${niveau + 1}`);

  const deplacer = (
    code: string,
    niveau: number | null,
    echangeAvec?: string,
  ) => {
    if (lectureSeule) return;
    const suivant = placer(placements, { code, niveau, echangeAvec }, pyramide);
    const renvoyes = itemsDeplacesParEchange(placements, suivant, code);
    onChange(suivant);
    setSelection(null);
    setNiveauPlein(null);
    // Un échange déplace un item que l'utilisateur n'a pas touché : sans le
    // dire, il croirait l'avoir perdu. Le simple placement, lui, se voit.
    const renvoye = renvoyes[0];
    const occupees = niveau === null ? 0 : Object.values(suivant).filter((n) => n === niveau).length;
    setAnnonce(
      niveau === null
        ? `${nomDe(code)} retourne dans «\u00a0À classer\u00a0».`
        : // Tournure sans accord : les items sont de tous genres (« Autonomie », « Travail d'équipe »).
          `${nomNiveau(niveau)} reçoit ${nomDe(code)} (${occupees} sur ${pyramide[niveau] ?? 0}).`,
    );
    setEchange(
      renvoye
        ? {
            texte:
              renvoye.vers === null
                ? `${nomDe(code)} remplace ${nomDe(renvoye.code)}, qui retourne dans « À classer ».`
                : `${nomDe(code)} et ${nomDe(renvoye.code)} ont échangé leur place : ${nomDe(renvoye.code)} passe en ${nomNiveau(renvoye.vers)}.`,
            deplace: renvoye.code,
            precedent: placements,
          }
        : null,
    );
  };

  const annulerEchange = () => {
    if (!echange || lectureSeule) return;
    onChange(echange.precedent);
    setEchange(null);
    // Sans ça, la ligne d'état (région `aria-live`) continuait d'annoncer le
    // placement qu'on vient justement d'annuler.
    setAnnonce("Échange annulé : chaque item a retrouvé sa place.");
  };

  /**
   * Tap sur un item : sélectionne, désélectionne, change de sélection, ou
   * échange avec la sélection quand les deux ne sont pas au même endroit.
   */
  const tapItem = (code: string) => {
    if (lectureSeule) return;
    if (selection === code) {
      setSelection(null);
      setNiveauPlein(null);
      return;
    }
    if (selection !== null) {
      const cible = placements[code] ?? null;
      // Deux items au même endroit : échanger ne changerait rien, l'intention
      // est de sélectionner l'autre.
      if (cible === (placements[selection] ?? null)) {
        setSelection(code);
        setNiveauPlein(null);
        return;
      }
      deplacer(selection, cible, code);
      return;
    }
    setSelection(code);
  };

  /**
   * Tap sur un niveau : y place la sélection courante. Un niveau PLEIN ne
   * remplace pas d'office un occupant choisi au hasard — on demande lequel.
   */
  const tapNiveau = (niveau: number | null): boolean => {
    if (selection === null) return false;
    const plein =
      niveau !== null &&
      placements[selection] !== niveau &&
      comptes[niveau]! >= pyramide[niveau]!;
    if (plein) {
      setNiveauPlein(niveau);
      return false;
    }
    deplacer(selection, niveau);
    return true;
  };

  const varianteDe = (code: string): VarianteChip => {
    if (niveauPlein !== null && placements[code] === niveauPlein) return "remplacable";
    if (echange?.deplace === code) return "deplace";
    return "normal";
  };

  /** Fin d'un drag : `idCible` est l'id dnd-kit de la zone ou de l'item survolé. */
  const deposer = (code: string, idCible: string) => {
    const cible = cibleDuDrop(idCible);
    if (!cible) return;
    if (cible.echangeAvec !== undefined) {
      // Dépôt sur un item : on vise SON niveau, en le désignant comme échange.
      if (cible.echangeAvec === code) return;
      deplacer(code, placements[cible.echangeAvec] ?? null, cible.echangeAvec);
      return;
    }
    deplacer(code, cible.niveau);
  };

  const consigne =
    selection !== null && niveauPlein !== null
      ? `${nomNiveau(niveauPlein)} est complet : choisissez l'item que ${nomDe(selection)} doit remplacer.`
      : selection !== null
      ? `Sélection : ${nomDe(selection)} — choisissez maintenant un niveau.`
      : annonce !== null
      ? annonce
      : isMobile
        ? "Touchez un item pour choisir son niveau."
        : "Glissez un item vers un niveau, ou cliquez-le puis cliquez un niveau.";

  const abandonnerSelection = () => {
    setSelection(null);
    setNiveauPlein(null);
  };

  /** Revenir au choix du niveau, sans remplacer personne dans le niveau plein. */
  const quitterRemplacement = () => setNiveauPlein(null);

  return {
    selection,
    niveauPlein,
    abandonnerSelection,
    quitterRemplacement,
    echange,
    consigne,
    tapItem,
    tapNiveau,
    deposer,
    annulerEchange,
    varianteDe,
  };
}
